import { getStyle, resolveText, uid, wrapText } from "./studioModel";
import { groupPath } from "./studioGroups";
import { textWidth } from "./studioTextFit";

const visible = (n) => !n.hidden && n.opacity !== 0;
const isName = (n) => n.type === "text" && n.role === "dish" && /[\p{L}\p{N}]/u.test(n.text || n.name || "");
const isDot = (n) => n.type === "text" && /^[\s•·]+$/.test(n.text || "");
const round = (n) => Math.round(n * 10000) / 10000;
const commonGroup = (a, b) => groupPath(a).some((g) => groupPath(b).some((h) => g.id === h.id));
const originalFits = (n, style, text) => n.originalLayout && n.originalLayout.text === text && n.w === n.originalLayout.w && n.h === n.originalLayout.h && Object.entries(n.originalLayout.style).every(([k, v]) => style[k] === v);
const widthFor = (n, doc, content) => Math.max(1, ...String(resolveText(n, content) || "").split("\n").map((line) => textWidth(line, getStyle(doc, n)))) + 3;
const lineHeight = (n, doc) => { const s = getStyle(doc, n); return s.fontSize * (s.lineHeight || 1.2); };
function ink(n, doc, content) {
  const s = getStyle(doc, n), text = String(resolveText(n, content) || "");
  const lines = wrapText(text, s, n.w, (t) => textWidth(t, s));
  const original = originalFits(n, s, text);
  const firstWidth = original ? textWidth(n.originalLayout.runs[0]?.text || "", s) : textWidth(lines[0] || "", s);
  const offset = original ? n.originalLayout.runs[0]?.x || 0 : s.align === "center" ? (n.w - firstWidth) / 2 : s.align === "right" ? n.w - firstWidth : 0;
  const baseline = original ? n.originalLayout.runs[0]?.y || s.fontSize * .86 : s.fontSize * .86;
  return { x: n.x + offset, right: n.x + offset + firstWidth, baseline: n.y + baseline, height: s.fontSize * 1.3 + Math.max(0, lines.length - 1) * lineHeight(n, doc), lines };
}

// An explicit repair discovers existing inline relationships once. Subsequent
// layout uses those relationships rather than guessing from moving rectangles.
export function connectInlineText(doc, content) {
  return { ...doc, pages: doc.pages.map((page) => {
    let elements = page.elements.map((n) => ({ ...n }));
    const prices = elements.filter((n) => visible(n) && n.type === "text" && n.role === "price" && !n.inlineRow);
    prices.forEach((price) => {
      const names = elements.filter((n) => visible(n) && isName(n) && !n.inlineRow && Math.abs(n.y - price.y) < 4 && commonGroup(n, price)).sort((a, b) => a.x - b.x);
      if (!names.length) return;
      let dots = elements.filter((n) => visible(n) && isDot(n) && Math.abs(n.y - price.y) < 4 && commonGroup(n, price));
      if (dots.length === 1 && (dots[0].text.match(/[•·]/g) || []).length > 1) {
        const source = dots[0], count = (source.text.match(/[•·]/g) || []).length;
        dots = Array.from({ length: count }, (_, i) => ({ ...source, id: i ? uid() : source.id, text: "•", name: "Separator", role: "note", originalLayout: undefined, originalText: undefined }));
        elements = elements.flatMap((n) => n.id === source.id ? dots : [n]);
      }
      const ordered = [];
      names.forEach((name, i) => { ordered.push(name); if (dots[i]) ordered.push(dots[i]); });
      ordered.push(price);
      const align = names.length === 1 && getStyle(doc, names[0]).align === "center" ? "center" : "left";
      ordered.forEach((n, order) => {
        n.inlineRow = { id: names[0].id, order, align };
        if (isDot(n)) { n.text = "•"; n.name = "Separator"; n.role = "note"; delete n.originalLayout; delete n.originalText; }
      });
    });
    elements = elements.map((n) => {
      const name = elements.find((a) => a.id === n.attachedTo && isName(a));
      if (!name || n.nameAnchor || !/^gluten[ -]free$/i.test(n.name || "")) return n;
      const size = getStyle(doc, name).fontSize;
      return { ...n, nameAnchor: { gap: 3, widthRatio: n.w / size, heightRatio: n.h / size } };
    });
    return { ...page, elements };
  }) };
}

export function alignNameMarkers(doc, content) {
  return { ...doc, pages: doc.pages.map((page) => {
    const byId = new Map(page.elements.map((n) => [n.id, n]));
    return { ...page, elements: page.elements.map((n) => {
      const name = byId.get(n.attachedTo);
      if (!n.nameAnchor || !name || n.locked) return n;
      const s = getStyle(doc, name), m = ink(name, doc, content);
      const ratio = (value, fallback) => Number.isFinite(value) && value > 0 && value < 4 ? value : fallback;
      const w = s.fontSize * ratio(n.nameAnchor.widthRatio, 2 / 3), h = s.fontSize * ratio(n.nameAnchor.heightRatio, 2 / 3);
      const gap = Number.isFinite(n.nameAnchor.gap) ? Math.max(0, n.nameAnchor.gap) : 3;
      // Centre the mark on the cap height (≈0.63em above the baseline in
      // Cormorant), not on the full line box, so it sits level with the name.
      // side "right" mirrors the mark after the name's first line.
      const x = n.nameAnchor.side === "right" ? m.right + gap : m.x - gap - w, y = m.baseline - s.fontSize * .315 - h / 2;
      const cx = name.x + name.w / 2, cy = name.y + name.h / 2, a = (name.rotation || 0) * Math.PI / 180;
      const dx = x + w / 2 - cx, dy = y + h / 2 - cy;
      return { ...n, x: round(cx + dx * Math.cos(a) - dy * Math.sin(a) - w / 2), y: round(cy + dx * Math.sin(a) + dy * Math.cos(a) - h / 2), w: round(w), h: round(h), rotation: name.rotation || 0 };
    }) };
  }) };
}

// Keep the same row anchor as type grows/shrinks. Names and prices retain their
// individual editable nodes; a separator gets only the width of its single dot.
export function fitLinkedText(doc, content, { connect = false } = {}) {
  const source = connect ? connectInlineText(doc, content) : doc;
  let next = { ...source, pages: source.pages.map((page) => {
    const elements = page.elements.map((n) => ({ ...n, style: { ...n.style } }));
    const rows = new Map();
    elements.filter((n) => visible(n) && n.inlineRow).forEach((n) => {
      if (!rows.has(n.inlineRow.id)) rows.set(n.inlineRow.id, []);
      rows.get(n.inlineRow.id).push(n);
    });
    const moveBelow = (row, threshold, dy, left, right) => {
      if (Math.abs(dy) < .001) return;
      elements.forEach((n) => {
        if (n.locked || row.includes(n) || n.nameAnchor || n.type !== "text" || n.y < threshold - .1 || n.x >= right || n.x + n.w <= left) return;
        n.y = round(n.y + dy);
      });
    };
    [...rows.values()].sort((a, b) => Math.min(...a.map((n) => n.y)) - Math.min(...b.map((n) => n.y))).forEach((nodes) => {
      nodes.sort((a, b) => a.inlineRow.order - b.inlineRow.order);
      if (nodes.some((n) => n.locked || n.rotation) || !nodes.some(isName)) return;
      const anchor = nodes.find(isName), align = anchor.inlineRow.align;
      const boundsNodes = nodes.filter((n) => !isDot(n));
      const left = Math.min(...boundsNodes.map((n) => n.x)), right = Math.max(...boundsNodes.map((n) => n.x + n.w));
      // A wine name initially has a full-column frame. Its row is left aligned,
      // so only the left edge is significant during the initial repair.
      const center = (left + right) / 2;
      const maxWidth = align === "center" ? 2 * Math.min(center - page.margin, page.width - page.margin - center) : page.width - Math.max(page.margin, left) - Math.max(page.margin, left);
      const available = Math.max(24, maxWidth), gap = 6;
      const baseline = ink(anchor, source, content).baseline;
      const oldHeight = Number.isFinite(anchor.inlineRow.height) ? anchor.inlineRow.height : Math.max(...nodes.map((n) => getStyle(source, n).fontSize * 1.3));
      const widths = nodes.map((n) => widthFor(n, source, content));
      const names = nodes.filter(isName);
      // Reserve the price on the first line of a long wine name. Wrapping only
      // happens when the full name truly cannot fit within the printed column.
      if (names.length === 1) {
        const i = nodes.indexOf(anchor), other = widths.reduce((a, w, j) => j === i ? a : a + w, 0) + gap * (nodes.length - 1);
        widths[i] = Math.min(widths[i], Math.max(24, available - other));
      }
      let lines = [[]], used = 0;
      nodes.forEach((n, i) => {
        const w = Math.min(available, widths[i]);
        if (names.length > 1 && used && used + gap + w > available && !isDot(n)) { lines.push([]); used = 0; }
        lines[lines.length - 1].push({ n, w }); used += (used ? gap : 0) + w;
      });
      let lineBaseline = baseline, totalHeight = 0;
      lines.forEach((line) => {
        const fullWidth = line.reduce((sum, { w }) => sum + w, 0) + gap * (line.length - 1);
        let x = align === "center" ? center - fullWidth / 2 : left;
        let height = 0;
        line.forEach(({ n, w }) => {
          const s = getStyle(source, n);
          n.x = round(x); n.w = round(w); n.y = round(lineBaseline - s.fontSize * .86);
          delete n.originalLayout;
          const m = ink(n, source, content); height = Math.max(height, m.height);
          n.h = round(Math.max(s.fontSize * 1.3, m.height) + 1);
          x += w + gap;
        });
        totalHeight += height + (totalHeight ? 3 : 0); lineBaseline += height + 3;
      });
      const delta = totalHeight - oldHeight;
      anchor.inlineRow = { ...anchor.inlineRow, height: round(totalHeight) };
      // Preserve the section's existing vertical rhythm as wrapped row heights
      // change. Columns beside this row are left alone.
      moveBelow(nodes, baseline + oldHeight - getStyle(source, anchor).fontSize * .86 - 1, delta, align === "center" ? center - available / 2 : left, align === "center" ? center + available / 2 : left + available);
    });
    // Free-standing item names grow around their alignment anchor, bounded by
    // their column. Descriptions/prices move only when a name needs extra lines.
    elements.filter((n) => visible(n) && isName(n) && !n.inlineRow && !n.locked && (connect || n.nameTextHeight !== undefined)).forEach((n) => {
      const s = getStyle(source, n), center = n.x + n.w / 2;
      const peers = elements.filter((p) => p !== n && visible(p) && isName(p) && Math.abs(p.y - n.y) < 12 && Math.abs(center - (p.x + p.w / 2)) > 24);
      const column = peers.length ? Math.min(...peers.map((p) => Math.abs(center - (p.x + p.w / 2)))) - 8 : page.width;
      const maximum = s.align === "center" ? Math.min(column, 2 * Math.min(center - page.margin, page.width - page.margin - center)) : Math.min(column, page.width - page.margin - n.x);
      // A frame the designer sized by hand (say, three deliberate lines) is
      // left alone while its text still fits inside it. Only text that spills
      // out of the frame widens it, and only the spill pushes rows below down.
      const oldInfo = ink(n, source, content), frameHeight = Math.max(n.h - 1, s.fontSize * 1.3);
      if (oldInfo.height <= frameHeight + .5) { n.nameTextHeight = round(oldInfo.height); return; }
      const needed = widthFor(n, source, content), w = Math.min(Math.max(n.w, needed), Math.max(24, maximum));
      if (Math.abs(w - n.w) > .001) {
        n.x = round(n.x - (w - n.w) * (s.align === "center" ? .5 : s.align === "right" ? 1 : 0)); n.w = round(w); delete n.originalLayout;
      }
      const m = ink(n, source, content);
      const delta = Math.max(0, m.height - frameHeight);
      if (delta) moveBelow([n], oldInfo.baseline + frameHeight - s.fontSize * .86 - 1, delta, n.x, n.x + n.w);
      n.nameTextHeight = round(m.height);
      n.h = round(Math.max(n.h, m.height + 1));
    });
    return { ...page, elements };
  }) };
  return alignNameMarkers(next, content);
}
